import logo from 'assets/header-prevencor-xx.png';
import AssinaturaDigital from 'components/core/AssinaturaDigital';
import AutosaveIndicator from 'components/core/AutosaveIndicator';
import CustomFileInput from 'components/laudos/CustomFileInput';
import LaudoActionButtons from 'components/laudos/LaudoActionButtons';
import { AUTOSAVE_STATUS, useAutosave } from 'hooks/useAutosave';
import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Modal, Spinner } from 'react-bootstrap';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from 'services/api';
import { selectEcgById } from 'services/ecg';
import 'styles/laudo-base.css';
import useSWR from 'swr';
import { enviarArquivo, toWhatsappNumber } from 'utils/evolution';
import { localDraftStorage } from 'utils/localDraftStorage';
import { baixarBlobPdf, blobToDataUrl, pdfMakeGetBase64, pdfMakeGetBlob } from 'utils/pdfBlob';
import { generateEcgPdf } from './generateEcgPdf';

export default function FormEcg() {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const {
    data: ecgById,
    isLoading: loadingEcgById,
    mutate: mutateEcg,
  } = useSWR(id ? ['ecg', id] : null, () => selectEcgById(id), {
    revalidateOnFocus: false,
  });

  const [ecg, setEcg] = useState(location.state?.data || null);
  const [version, setVersion] = useState(1);
  const [situacao, setSituacao] = useState('PARCIAL');
  const [hashIntegridade, setHashIntegridade] = useState(null);
  const [observacao, setObservacao] = useState('');

  // Modais de controle
  const [localDraft, setLocalDraft] = useState(null);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [showConfirmDefinitivo, setShowConfirmDefinitivo] = useState(false);
  const [showModalAnexo, setShowModalAnexo] = useState(false);
  const [fileToUpload, setFileToUpload] = useState([]);
  const [loadingUploadImage, setLoadingUploadImage] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [currentImageBase64, setCurrentImageBase64] = useState(null);
  const [currentImageKey, setCurrentImageKey] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const isDefinitivo = situacao === 'DEFINITIVO';

  const applyEcgRecord = (record) => {
    if (!record) return;
    setEcg(record);
    setObservacao(record.OBSERVACAO || '');
    setVersion(record.VERSION || 1);
    setSituacao(record.SITUACAO || 'PARCIAL');
    setHashIntegridade(record.HASH_INTEGRIDADE || null);
  };

  useEffect(() => {
    if (!ecgById) return;

    applyEcgRecord(ecgById);

    if (ecgById.SITUACAO !== 'DEFINITIVO') {
      const draft = localDraftStorage.getDraft('FormECG', ecgById.CODECG);
      if (draft && draft.data !== ecgById.OBSERVACAO) {
        setLocalDraft(draft);
        setShowDraftModal(true);
      }
    }
  }, [ecgById]);

  useEffect(() => {
    if (ecg?.CODECG && !isDefinitivo && observacao) {
      localDraftStorage.saveDraft('FormECG', ecg.CODECG, observacao);
    }
  }, [observacao, ecg?.CODECG, isDefinitivo]);

  const syncEcgFromServer = async () => {
    if (!ecg?.CODECG) return null;
    const fresh = await mutateEcg();
    applyEcgRecord(fresh);
    return fresh;
  };

  const handleSave = async (options = {}) => {
    try {
      const isTargetDefinitivo = options?.situacaoTarget === 'DEFINITIVO';

      const payload = {
        observacao,
        codMedico: ecg?.CODREALIZOU,
        medico: ecg?.REALIZOU,
        dataEcg: ecg?.DATAECG,
        // Override após flush parcial — setState ainda não refletiu
        version: options?.version ?? version,
        situacao: isTargetDefinitivo ? 'DEFINITIVO' : 'PARCIAL',
      };

      const response = await api.put(`/ecg/${ecg.CODECG}`, payload);

      if (response.data?.data) {
        setVersion(response.data.data.version);
        setSituacao(response.data.data.situacao);
        if (response.data.data.hashIntegridade) {
          setHashIntegridade(response.data.data.hashIntegridade);
        }
      }

      if (options?.isManual !== false) {
        toast.success(response.data.message);
      }

      return response.data;
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error(
          error.response?.data?.message ||
            'Este laudo foi alterado por outro usuário. Recarregue a página para ver a versão mais recente.'
        );
      } else if (error.response?.status === 403) {
        toast.error(error.response.data.message);
      } else if (options?.isManual !== false) {
        toast.error('Falha ao salvar o laudo.');
      }
      // Repropaga para o autosave marcar ERROR; callers manuais devem capturar
      throw error;
    }
  };

  const {
    status: autosaveStatus,
    errorMessage: autosaveError,
    manualSave,
    retrySave,
  } = useAutosave({
    recordId: ecg?.CODECG,
    data: observacao,
    version,
    onSave: (payload) => handleSave({ ...payload, situacaoTarget: 'PARCIAL' }),
    debounceMs: 3000,
    enabled: !isDefinitivo,
  });

  const handleGravarDefinitivo = async () => {
    setShowConfirmDefinitivo(false);
    try {
      // Flush parcial sincroniza VERSION (evita 409 por race com autosave)
      const parcial = await handleSave({
        situacaoTarget: 'PARCIAL',
        isManual: false,
      });
      await handleSave({
        situacaoTarget: 'DEFINITIVO',
        version: parcial?.data?.version,
      });
      localDraftStorage.removeDraft('FormECG', ecg?.CODECG);
    } catch (error) {
      if (error.response?.status === 409) {
        await syncEcgFromServer().catch(() => {});
      }
      // Evita unhandledrejection (Sentry DR-ORNELLAS-25F)
    }
  };

  const handleRestoreDraft = () => {
    if (localDraft) {
      setObservacao(localDraft.data);
      toast.info('Rascunho restaurado!');
    }
    setShowDraftModal(false);
  };

  const handleDiscardDraft = () => {
    if (ecg?.CODECG) {
      localDraftStorage.removeDraft('FormECG', ecg.CODECG);
    }
    setShowDraftModal(false);
  };

  useEffect(() => {
    const fetchExistingImage = async () => {
      if (!ecg?.CODECG) return;

      try {
        setLoadingImage(true);
        const { data } = await api.get('/arquivos/presign-view', {
          params: { codLaudo: ecg.CODECG, procedimento: 'ecg' },
        });

        setCurrentImageUrl(data?.url);
        setCurrentImageKey(data?.key);

        try {
          const response = await fetch(data?.url);
          const blob = await response?.blob();
          const base64 = await blobToDataUrl(blob);
          setCurrentImageBase64(base64);
        } catch {
          toast.warn('Erro na conversão da imagem!');
        }
      } catch {
        // Nenhuma imagem
      } finally {
        setLoadingImage(false);
      }
    };

    fetchExistingImage();
  }, [ecg?.CODECG]);

  async function handleDeleteImage() {
    if (!currentImageKey || isDefinitivo) return;

    try {
      const res = await api.delete('/arquivos', { data: { key: currentImageKey } });
      toast.success(res.data.message);
      setCurrentImageUrl(null);
      setCurrentImageKey(null);
      setCurrentImageBase64(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao deletar imagem!');
    }
  }

  async function handleUploadFile(file) {
    if (isDefinitivo) return false;
    if (!file.length) {
      toast.warn('Selecione um arquivo!');
      return false;
    }

    const fileExts = ['.jpg', '.jpeg', '.bmp', '.BMP'];
    const isInvalid = !fileExts.some((ext) => file[0].name.endsWith(ext));
    if (isInvalid) {
      toast.warn('Selecione apenas arquivos .jpg ou .bmp.');
      return false;
    }

    try {
      setLoadingUploadImage(true);

      const base64 = await blobToDataUrl(file[0]);
      setCurrentImageBase64(base64);

      const { data } = await api.post('/arquivos/presign-upload', {
        codLaudo: ecg?.CODECG,
        procedimento: 'ecg',
        filename: file[0].name,
        contentType: file[0].type,
      });

      await fetch(data.url, { method: 'PUT', body: file[0] });

      const viewResponse = await api.get('/arquivos/presign-view', {
        params: {
          codLaudo: ecg?.CODECG,
          procedimento: 'ecg',
          filename: data.key.split('/').pop(),
        },
      });

      setCurrentImageUrl(viewResponse.data.url);
      setCurrentImageKey(viewResponse.data.key);
      setShowModalAnexo(false);
      toast.success('Arquivo enviado com sucesso!');
      return true;
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao enviar arquivo!');
      return false;
    } finally {
      setLoadingUploadImage(false);
    }
  }

  const buildEcgParaPdf = () => ({
    ...ecg,
    OBSERVACAO: observacao,
    HASH: hashIntegridade,
  });

  async function handleEnviarEcg() {
    if (!ecg || !isDefinitivo) return;

    const celularWhats = toWhatsappNumber(ecg.CELULARPAC);
    if (!celularWhats) {
      toast.warn('Número de celular não encontrado!');
      return;
    }

    try {
      const pdfDocGenerator = await generateEcgPdf({
        ecg: buildEcgParaPdf(),
        currentImageBase64,
      });

      const pdfBase64 = await pdfMakeGetBase64(pdfDocGenerator);
      const payload = {
        to: celularWhats,
        pdfBase64,
        paciente: {
          id: ecg?.CODPAC,
          nome: ecg?.NOMEPAC,
          medico: ecg?.REALIZOU,
        },
        codLaudo: ecg?.CODECG,
        fileName: `Laudo_ECG_${ecg?.CODECG}.pdf`,
        caption: `Olá *${ecg?.NOMEPAC}*, tudo bem? 😊\n\nSegue em anexo o seu *Laudo de Eletrocardiograma*.\n\nQualquer dúvida, nossa equipe estará à disposição no setor de atendimento: (14) 3402-2700`,
      };

      const result = await enviarArquivo(payload);
      if (result.success) {
        toast.success(result.message);
        return;
      }

      toast.error(result.message);
    } catch {
      toast.error(
        'Erro ao gerar PDF para envio! A imagem pode ser muito grande — tente reanexar em JPG.'
      );
    }
  }

  async function handleImprimirEcg() {
    if (!ecg || !isDefinitivo) return;

    try {
      const pdfDocGenerator = await generateEcgPdf({
        ecg: buildEcgParaPdf(),
        currentImageBase64,
      });
      const blob = await pdfMakeGetBlob(pdfDocGenerator);
      baixarBlobPdf({
        blob,
        fileName: `ECG-${ecg.CODECG}-${ecg.NOMEPAC}.pdf`,
      });
    } catch {
      toast.error('Erro ao gerar PDF! A imagem pode ser muito grande — tente reanexar em JPG.');
    }
  }

  if (!ecg && loadingEcgById) {
    return (
      <Card className="mb-2 mt-2">
        <Card.Body>
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (!ecg) {
    return (
      <Card className="mb-2 mt-2">
        <Card.Body className="text-center text-muted py-5">Laudo ECG não encontrado.</Card.Body>
      </Card>
    );
  }

  const isSaving = autosaveStatus === AUTOSAVE_STATUS.SAVING;

  return (
    <>
      <Card className="mb-2 mt-2">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div style={{ flex: 1 }}>
            {isDefinitivo ? (
              <Badge bg="success" className="p-2" style={{ fontSize: '0.85rem' }}>
                ✓ LAUDO DEFINITIVO
              </Badge>
            ) : (
              <Badge bg="warning" className="p-2" style={{ fontSize: '0.85rem' }}>
                EM EDIÇÃO (PARCIAL)
              </Badge>
            )}
          </div>
          <div className="text-center font-weight-bold" style={{ flex: 2, fontSize: '1.9rem' }}>
            <img
              src={logo}
              alt="Prevencor"
              style={{ display: 'block', maxWidth: '100%', margin: '0 auto 8px' }}
              className="no-print"
            />
            LAUDO DE ELETROCARDIOGRAMA
          </div>
          <div className="text-right no-print" style={{ flex: 1 }}>
            {!isDefinitivo && (
              <AutosaveIndicator
                status={autosaveStatus}
                errorMessage={autosaveError}
                onRetry={retrySave}
              />
            )}
          </div>
        </Card.Header>

        <Card.Body>
          <strong className="mr-auto">Nome do Paciente: </strong>
          {`${ecg.CODPAC} - ${ecg.NOMEPAC}`}
          <br />
          <strong className="mr-auto">Data de Nasc: </strong>
          {`${ecg.dataNascimento}`}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <strong className="mr-auto">Sexo: </strong>
          {ecg.SEXOPAC === 'M' ? 'Masculino' : 'Feminino'}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <strong className="mr-auto">Idade: </strong>
          {`${ecg.idadePaciente} anos`}
          <br />
          <strong className="mr-auto">Convênio: </strong>
          {ecg.SIGLACONV}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <strong className="mr-auto">Cod ECG: </strong>
          {ecg.CODECG}&nbsp;&nbsp;&nbsp;
          <strong className="mr-auto">Data do Exame: </strong>
          {ecg.dataEcg}
          <br />
          <strong className="mr-auto">Médico que Realizou: Dr. </strong>
          {ecg.REALIZOU}
          <br />
          <strong className="mr-auto">Médico que Solicitou: Dr. </strong>
          {ecg.SOLICITOU}
          <hr />
          {loadingImage ? (
            <div className="text-center p-3">
              <Spinner animation="border" variant="primary" />
              <p>Carregando imagem...</p>
            </div>
          ) : currentImageUrl ? (
            <div className="text-center p-2">
              <img
                src={currentImageUrl}
                alt="Eletrocardiograma"
                style={{ maxWidth: '100%', maxHeight: '100%' }}
                className="img-fluid"
              />
              {!isDefinitivo && (
                <div className="mt-1 no-print">
                  <Button variant="danger" size="sm" onClick={handleDeleteImage}>
                    Remover imagem
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center p-3 text-muted">
              <p>Nenhuma imagem anexada</p>
            </div>
          )}
          <div>
            <strong>Descrição do Exame</strong>
            <textarea
              className="form-control mt-2"
              rows={6}
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              placeholder="Digite o laudo do exame..."
              disabled={isDefinitivo}
            />
          </div>
        </Card.Body>

        <Card.Footer className="d-flex justify-content-end align-items-center no-print">
          <LaudoActionButtons
            actions={[
              {
                key: 'voltar',
                label: 'Voltar',
                variant: 'outline-secondary',
                icon: 'bi bi-arrow-left',
                onClick: () => history.goBack(),
              },
              ...(!isDefinitivo
                ? [
                    {
                      key: 'save_parcial',
                      label: isSaving ? 'Salvando...' : 'Gravar Parcial',
                      variant: 'secondary',
                      icon: 'bi bi-save',
                      onClick: manualSave,
                      disabled: isSaving,
                    },
                    {
                      key: 'save_definitivo',
                      label: 'Gravar Definitivo',
                      variant: 'success',
                      icon: 'bi bi-check-circle',
                      onClick: () => setShowConfirmDefinitivo(true),
                      disabled: isSaving,
                    },
                  ]
                : []),
              {
                key: 'timbrado',
                label: 'Timbrado',
                icon: 'bi bi-file-earmark-pdf',
                onClick: () => handleImprimirEcg(),
                disabled: !isDefinitivo,
              },
              ...(!isDefinitivo
                ? [
                    {
                      key: 'anexar',
                      label: 'Anexar',
                      variant: 'warning',
                      icon: 'bi bi-paperclip',
                      onClick: () => setShowModalAnexo(true),
                    },
                  ]
                : []),
              {
                key: 'enviar',
                label: 'Enviar',
                variant: 'success',
                icon: 'bi bi-whatsapp',
                onClick: handleEnviarEcg,
                disabled: !isDefinitivo,
              },
            ]}
          />
        </Card.Footer>

        <Card.Footer className="text-center">
          {isDefinitivo ? (
            <AssinaturaDigital
              hash={hashIntegridade || ecg?.HASH_INTEGRIDADE}
              nome={ecg?.REALIZOU}
              showPrintButton={false}
            />
          ) : (
            <small className="text-muted italic">
              Aguardando gravação e assinatura definitiva para gerar o Hash de Integridade clínica.
            </small>
          )}
        </Card.Footer>
      </Card>

      {/* Modal de Confirmação para Gravar Definitivo */}
      <Modal show={showConfirmDefinitivo} onHide={() => setShowConfirmDefinitivo(false)} centered>
        <Modal.Header closeButton className="bg-warning text-dark">
          <Modal.Title>Finalizar Laudo Definitivo?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Atenção: Ao assinar e gravar como <strong>DEFINITIVO</strong>, este laudo não poderá mais
          ser editado e será assinado eletronicamente com o código Hash de integridade.
          <br />
          <br />
          Deseja prosseguir?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmDefinitivo(false)}>
            Cancelar
          </Button>
          <Button variant="success" onClick={handleGravarDefinitivo} disabled={isSaving}>
            Confirmar e Assinar Definitivo
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Restauração de Rascunho Local */}
      <Modal show={showDraftModal} onHide={handleDiscardDraft} centered>
        <Modal.Header closeButton>
          <Modal.Title>Conteúdo não sincronizado encontrado</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Foi encontrado um rascunho salvo neste navegador com alterações mais recentes.
          <br />
          <small className="text-muted">
            Última alteração local:{' '}
            {localDraft?.updatedAt ? new Date(localDraft.updatedAt).toLocaleString('pt-BR') : 'N/A'}
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDiscardDraft}>
            Descartar
          </Button>
          <Button variant="primary" onClick={handleRestoreDraft}>
            Restaurar alterações
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Anexo */}
      <Modal
        show={showModalAnexo}
        centered
        onHide={() => {
          setShowModalAnexo(false);
          setFileToUpload([]);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Anexar arquivo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CustomFileInput
            id="file-upload"
            label="Selecionar arquivo"
            multiple={false}
            onChange={(e) => setFileToUpload(Array.from(e.target.files))}
          />
          <ul>
            {fileToUpload && fileToUpload.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <li style={{ marginRight: 5 }}>{fileToUpload[0].name}</li>
                <span
                  style={{ cursor: 'pointer', color: 'red' }}
                  onClick={() => setFileToUpload([])}
                >
                  X
                </span>
              </div>
            )}
          </ul>
          {currentImageUrl && (
            <Alert variant="warning">
              <Alert.Heading>Eletro já tem imagem!</Alert.Heading>
              <p>Delete a atual para anexar outra</p>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          {loadingUploadImage ? (
            <Spinner animation="border" variant="dark" />
          ) : (
            <Button
              onClick={() => handleUploadFile(fileToUpload)}
              disabled={Boolean(currentImageUrl)}
            >
              Confirmar
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}
